import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import Info from '@material-ui/icons/Info';
import ErrorIcon from '@material-ui/icons/Error';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import { withStyles } from '@material-ui/core/styles';
import CheckCircle from '@material-ui/icons/CheckCircle';
import DynamicString from '@passportinc/hydra-core/components/DynamicString';

/**
 * Toast message will disappear in ms
 */
const DEFAULT_TOAST_EXPIRE_TIME = 6000;

/**
 * Toast Context for provider / consumer
 */
export const ToastContext = React.createContext({});

export const styles = theme => ({
  root: {
    backgroundColor: theme.palette.background.white,
    padding: 0,
    margin: 0,
    maxWidth: 520,
    width: 520,
  },
  contentWrapper: {
    color: '#78798d',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    flex: 4,
    padding: '0px 15px',
  },
  success: {
    color: '#00ac2d',
  },
  info: {
    color: '#0b80ff',
  },
  error: {
    color: theme.palette.alert.main,
  },
  statusIcon: {
    width: '24px',
    height: '24px',
  },
  message: {
    ...theme.typography.body,
    paddingLeft: 20,
    fontSize: '14px',
    lineHeight: '1.43',
  },
  closeButton: {
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontSize: 14,
  },
  dismiss: {
    flex: 1,
  },
});

/**
 * Toast variants
 */
const VARIANTS = {
  info: 'info',
  success: 'success',
  error: 'error',
};

/**
 * Mapping from variant to icon
 */
const VARIANT_ICON_MAPPING = {
  [VARIANTS.info]: Info,
  [VARIANTS.success]: CheckCircle,
  [VARIANTS.error]: ErrorIcon,
};

/**
 * Toast Provider React Component
 * @returns {React.JSX} - ToastProvider
 */
export class ToastProvider extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    classes: PropTypes.shape({}).isRequired,
  }

  state = {
    notification: {},
    isToasted: false,
    autoCloseDuration: DEFAULT_TOAST_EXPIRE_TIME,
  };

  /**
   * Handles setting the toast state.
   * @param {Object<{ message, variant }>} notification - the notification object
   * @returns {void}
   */
  handleToast = notification => this.setState({
    isToasted: true,
    autoCloseDuration: _.get(notification, 'autoCloseDuration', DEFAULT_TOAST_EXPIRE_TIME),
    notification,
  });

  /**
   * Closes the toast state but keeps the toast message active.
   * This makes it so that the toast contains the message as the `exit` animation fires.
   * If you wipe the toast message state while it's closing, the toast will `collapse`
   * in on itself due to lack of content in the DOM.
   * @returns {void}
   */
  handleCloseToast = () => this.setState({
    isToasted: false,
  });

  /** @inheritDoc */
  render() {
    const { children, classes } = this.props;
    const { notification, isToasted, autoCloseDuration } = this.state;

    const Icon = _.get(VARIANT_ICON_MAPPING, notification.variant, VARIANT_ICON_MAPPING.success);

    return (
      <React.Fragment>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          ContentProps={{
            classes: { root: classes.root },
          }}
          open={isToasted}
          autoHideDuration={autoCloseDuration}
          onClose={this.handleCloseToast}
          message={
          (
            <div className={classes.contentWrapper}>
              <div className={classes.content}>
                <Icon
                  classes={{ root: classes[notification.variant] }}
                  className={cn(classes.statusIcon)}
                />
                <span className={classes.message}>{notification.message}</span>
              </div>
              <div className={classes.dismiss}>
                <Button
                  key="close"
                  onClick={this.handleCloseToast}
                  className={classes.closeButton}
                >
                  <DynamicString
                    lookupPath="hydra_permits_toast_provider_dismiss_button_text"
                    defaultText="Dismiss"
                  />
                </Button>
              </div>
            </div>
          )
          }
        />
        <ToastContext.Provider value={{
          notification,
          onToast: this.handleToast,
          VARIANTS,
        }}
        >
          {children}
        </ToastContext.Provider>
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(ToastProvider);
